import * as angular from 'angular';
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsCommands} from "../../shared/dto/aps.commands";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import UIUtilityServiceName, {EnumOption, UIUtilityService} from "../../services/utility/uiUtility.service";
import CheckListsServiceName, {CheckListsService} from "../../services/checkLists/checkLists.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NewGuidServiceName, {NewGuidService} from "../../services/utility/newguid.service";
import * as _ from "lodash";
import template from './checkEditor.tpl.html';
import { CheckItem } from '../../models/checkLists/checkListOverride.module';
import { CheckListController } from './checkList.controller';

let requires = [UIUtilityServiceName, CheckListsServiceName, NotifyServiceName, LoaderServiceName, NewGuidServiceName];

export interface CheckEditorParams {
  checkListId: string;
  checkList: CheckListController;
  check?: apsViewModels.CheckItem;
  checks: CheckItem[];
  headings: string[];
  isReadOnly: boolean;
  isShopCheckList: boolean;
}

export function openCheckEditor(data: CheckEditorParams,
                                $modal: mm.foundation.modal): angular.IPromise<apsCommands.CheckItemBase> {
  return $modal.open({
    template: template,
    controller: 'CheckEditorCtrl as vm',
    resolve: {
      data: () => data
    }
  }).result;
}

export interface CheckEditorCtrl {
  isNew: boolean;
  editedCheck: apsCommands.CheckItemBase;
  checkDataTypes: EnumOption[];
  checkEntryTypes: EnumOption[];
  defaultOutcomes: EnumOption[];
  additionalDataTypes: EnumOption[];
  metaDatas: EnumOption[];
  headings: string[];
  form: angular.IFormController;
  isShopCheckList: boolean;
  rangeChecks: CheckItem[];
  aggregateCheck?: CheckItem;
  passFailRangeCheck?: CheckItem;
  usePassFailRange: boolean;

  addChoice(): void;
  removeChoice(choice: apsCommands.AddCheckChoice): void;
  showNumberOfDecimals(): boolean;
  showRange(): boolean;
  showFrequency(): boolean;
  showFixedValues(): boolean;
  checkChoiceType(): string;
  showDefaultToToday(): boolean;
  isInstructions(): boolean;
  isBoolean(): boolean;
  save(): void;
  cancel(): void;
  dataTypeChange(): void;
  showAggregates(): boolean;
  addDimension(): void;
  removeDimension(index: number): void;
  dimsInvalid(): boolean;
  getValueTypes(): EnumOption[];
  getDefaultOutcomes(): EnumOption[];
  getAggregateFunctions(): EnumOption[];
  aggregateFunctionChanged(): void;
  hasAggregateFrequency(): boolean;
  getAggregateFrequencies(): EnumOption[];
  getAggregateChecks(): CheckItem[];
}

class CheckEditorController implements CheckEditorCtrl {
  private checkListsService: CheckListsService;
  private notifyService: NotifyService;
  private loaderService: LoaderService;
  private newGuidService: NewGuidService;
  private checkList: CheckListController;

  private $modalInst: mm.foundation.modalInstance;

  isNew: boolean;
  isReadOnly: boolean;
  editedCheck: apsCommands.CheckItemBase;
  checkDataTypes: EnumOption[];
  checkEntryTypes: EnumOption[];
  aggregateFunctions: EnumOption[];
  aggregateFrequencies: EnumOption[];
  severities: EnumOption[];
  valueTypes: EnumOption[];
  metaDatas: EnumOption[];
  defaultOutcomes: EnumOption[];
  additionalDataTypes: EnumOption[];
  headings: string[];
  form: angular.IFormController;
  isShopCheckList: boolean;
  rangeChecks: CheckItem[];
  aggregateCheck: CheckItem | null;
  passFailRangeCheck: CheckItem | null;
  usePassFailRange: boolean;
  data: CheckEditorParams;

  public constructor(
    $modalInstance: mm.foundation.modalInstance,
    checkListsService: CheckListsService,
    data: CheckEditorParams,
    notifyService: NotifyService,
    loaderService: LoaderService,
    uiUtilityService: UIUtilityService,
    newGuidService: NewGuidService) {

    this.$modalInst = $modalInstance;
    this.checkListsService = checkListsService;
    this.notifyService = notifyService;
    this.loaderService = loaderService;
    this.newGuidService = newGuidService;

    this.headings = data.headings;
    this.isNew = !data.check;
    this.isReadOnly = data.isReadOnly;
    this.isShopCheckList = data.isShopCheckList;
    this.data = data;
    this.checkList = data.checkList;

    this.checkDataTypes = uiUtilityService.getListOptionsFromEnum(apsEnums.CheckDataType, 1);
    this.checkEntryTypes = uiUtilityService.getListOptionsFromEnum(apsEnums.CheckEntry, 1);
    this.aggregateFunctions = uiUtilityService.getListOptionsFromEnum(apsEnums.AggregateFunction, 1);
    this.defaultOutcomes = uiUtilityService.getListOptionsFromEnum(apsEnums.CheckStatus, 1);
    this.metaDatas = uiUtilityService.getListOptionsFromEnum(apsEnums.CheckMetaData, 1);
    this.valueTypes = uiUtilityService.getListOptionsFromEnum(apsEnums.CheckValueType, 1);
    this.severities = uiUtilityService.getListOptionsFromEnum(apsEnums.Severity, 0);
    this.additionalDataTypes = uiUtilityService.getListOptionsFromEnum(apsEnums.CheckAdditionalData, 0);
    this.aggregateFrequencies = [{
      text: "Start of Shift",
      value: apsEnums.TimeUnits.Shift
    }, {
      text: "Start of Day",
      value: apsEnums.TimeUnits.Days
    }]

    this.rangeChecks = data.checks.filter(x => x.valueType === apsEnums.CheckValueType.Range);
    if (data.check) {
      this.rangeChecks = this.rangeChecks.filter(x => x.id !== data.check.id);
    }
    this.aggregateCheck = null;
    this.passFailRangeCheck = null;

    if (this.isNew) {
      this.editedCheck = {
        id: '',
        checkListId: data.checkListId,
        name: '',
        heading: '',
        description: '',
        dataType: undefined,
        defaultOutcome: apsEnums.CheckStatus.NotDone,
        allowComments: true,
        numberOfDecimalPlaces: undefined,
        entry: apsEnums.CheckEntry.PerShop,
        choices: <apsCommands.AddCheckChoice[]>[],
        rangeMin: undefined,
        rangeMax: undefined,
        frequencyFail: undefined,
        frequencyTotal: undefined,
        prepopulate: false,
        trueText: 'Yes',
        falseText: 'No',
        trueAdditionalData: null,
        falseAdditionalData: null,
        booleanPass: null,
        aggregatesCount: null,
        aggregateFunction: null,
        metaData: null,
        allowNotApplicable: false,
        valueType: apsEnums.CheckValueType.Single,
        dimensions: [],
        aggregateCheckId: null,
        aggregateFrequencyUnits: null,
        passFailRangeCheckId: null,
        defectTypeSeverity: null
      };

      this.newGuidService.getNewGuid('check')
        .then((newGuid) => {
          this.editedCheck.id = newGuid;
        });
    } else {
      this.editedCheck = _.clone<apsCommands.CheckItemBase>(data.check);
      this.editedCheck.choices = data.check.choices.concat();

      if (this.editedCheck.aggregatesCount === 0) {
        this.editedCheck.aggregatesCount = 1;
      }
      if (this.editedCheck.aggregateCheckId) {
        this.aggregateCheck = _.find(this.data.checks, x => x.id === this.editedCheck.aggregateCheckId);
        if (!this.aggregateCheck) {
          this.editedCheck.aggregateCheckId = null;
        }
      }
      if (this.editedCheck.passFailRangeCheckId) {
        this.passFailRangeCheck = _.find(this.rangeChecks, x => x.id === this.editedCheck.passFailRangeCheckId);
        if (this.passFailRangeCheck) {
          this.usePassFailRange = true;
        }
        else {
          this.editedCheck.passFailRangeCheckId = null;
        }
      }
    }
  }

  getAggregateChecks(): CheckItem[] {
    let checks = this.editedCheck.aggregateFunction === apsEnums.AggregateFunction.Cumulation
      ? this.data.checks.filter(x => x.aggregateFunction === null) 
      : this.data.checks.filter(x => x.entry !== apsEnums.CheckEntry.PerShop);
    if (!this.isNew) {
      checks = checks.filter(x => x.id !== this.editedCheck.id);
    }
    return checks;
  }

  aggregateFunctionChanged(): void {
    if (this.editedCheck.aggregateFunction === apsEnums.AggregateFunction.Cumulation) {
      return;
    }
    this.editedCheck.aggregateFrequencyUnits = null;
  }

  getAggregateFunctions(): EnumOption[] {
    let functions = this.aggregateFunctions;
    if (!this.checkList.isScheduled 
      || !this.checkList.frequencySelection
      || this.checkList.frequencySelection.frequencyUnits !== apsEnums.TimeUnits.Hours) {
      functions = functions.filter(x => 
        x.value !== apsEnums.AggregateFunction.Cumulation);
    }
    if (!this.isShopCheckList) {
      functions = functions.filter(x => x.value === apsEnums.AggregateFunction.Cumulation);
    }
    return functions;
  }

  hasAggregateFrequency(): boolean {
    return this.editedCheck.aggregateFunction === apsEnums.AggregateFunction.Cumulation;
  }

  getAggregateFrequencies(): EnumOption[] {
    return this.aggregateFrequencies;
  }

  getDefaultOutcomes(): EnumOption[] {
    if (this.editedCheck && this.editedCheck.allowNotApplicable) {
      return this.defaultOutcomes;
    }

    return _.filter(this.defaultOutcomes, vt => vt.value !== apsEnums.CheckStatus.NotApplicable);
  }

  getDataTypes(): EnumOption[] {
    let dataTypes = this.checkDataTypes;
    if (this.editedCheck.valueType === apsEnums.CheckValueType.Range) {
      return dataTypes.filter(x => x.value === apsEnums.CheckDataType.Text);
    }

    return dataTypes
  }

  dataTypeChange() {
    if (this.editedCheck.dataType === apsEnums.CheckDataType.Percent) {
      if (!this.editedCheck.rangeMin) {
        this.editedCheck.rangeMin = 0;
      }
      if (!this.editedCheck.rangeMax) {
        this.editedCheck.rangeMax = 100;
      }
    }
    if (this.editedCheck.dataType !== apsEnums.CheckDataType.DefectType) {
      this.editedCheck.defectTypeSeverity = null;
    }
  }

  getValueTypes(): EnumOption[] {
    let valueTypes = this.valueTypes;
    if (!this.showAggregates()) {
      valueTypes = _.filter(valueTypes, vt =>
        vt.value !== apsEnums.CheckValueType.Sample
        && vt.value !== apsEnums.CheckValueType.MultipleSample
        && vt.value !== apsEnums.CheckValueType.Aggregate);
    } else if (!this.isShopCheckList && (!this.checkList.isScheduled 
      || !this.checkList.frequencySelection
      || this.checkList.frequencySelection.frequencyUnits !== apsEnums.TimeUnits.Hours)) {
      valueTypes = _.filter(valueTypes, vt => vt.value !== apsEnums.CheckValueType.Aggregate);
    }

    return valueTypes
  }

  valueTypeChange() {
    if (this.editedCheck.valueType !== apsEnums.CheckValueType.Sample
      && this.editedCheck.valueType !== apsEnums.CheckValueType.MultipleSample
      && this.editedCheck.valueType !== apsEnums.CheckValueType.Aggregate) {
      this.editedCheck.aggregateFunction = null;
    }

    if (this.editedCheck.valueType !== apsEnums.CheckValueType.Sample
        && this.editedCheck.valueType !== apsEnums.CheckValueType.MultipleSample) {
      this.editedCheck.aggregatesCount = null;
      this.editedCheck.dimensions = [];
    }

    if (this.editedCheck.valueType === apsEnums.CheckValueType.Multiple
        || this.editedCheck.valueType === apsEnums.CheckValueType.MultipleSample) {
      this.editedCheck.dimensions = [
        {name: '', aggregateFunction: null},
        {name: '', aggregateFunction: null}
      ];
    }

    if (this.editedCheck.valueType === apsEnums.CheckValueType.Range) {
      this.editedCheck.dataType = apsEnums.CheckDataType.Text;
      this.editedCheck.entry = apsEnums.CheckEntry.PerShop;
    }
  }

  getEntryTypes(): EnumOption[] {
    let entryTypes = this.checkEntryTypes;
    if (this.editedCheck.valueType === apsEnums.CheckValueType.Range) {
      return entryTypes.filter(x => x.value === apsEnums.CheckEntry.PerShop);
    }

    if (this.editedCheck.valueType !== apsEnums.CheckValueType.Aggregate
        || !this.aggregateCheck) {
      return entryTypes;
    }

    switch (this.aggregateCheck.entry) {
      case apsEnums.CheckEntry.PerCavity:
        return entryTypes.filter(x => x.value !== apsEnums.CheckEntry.PerCavity);
      case apsEnums.CheckEntry.PerDrop:
      case apsEnums.CheckEntry.PerSection:
        return entryTypes.filter(x => x.value === apsEnums.CheckEntry.PerMachine
          || x.value === apsEnums.CheckEntry.PerShop);
      default:
        return entryTypes.filter(x => x.value === apsEnums.CheckEntry.PerShop);
    }
  }

  aggregateCheckChange() {
    if (this.aggregateCheck) {
      this.editedCheck.aggregateCheckId = this.aggregateCheck.id;
    } else {
      this.editedCheck.aggregateCheckId = null;
    }
  }

  passFailRangeCheckChange() {
    if (this.passFailRangeCheck) {
      this.editedCheck.passFailRangeCheckId = this.passFailRangeCheck.id;
    } else {
      this.editedCheck.passFailRangeCheckId = null;
    }
  }

  showNumberOfDecimals() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Decimal || this.editedCheck.dataType === apsEnums.CheckDataType.Percent;
  }

  showRange() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Decimal
      || this.editedCheck.dataType === apsEnums.CheckDataType.Integer
      || this.editedCheck.dataType === apsEnums.CheckDataType.Percent;
  }

  showFrequency() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Count;
  }

  showAllowNA() {
    return this.editedCheck.dataType !== apsEnums.CheckDataType.Instructions;
  }

  checkChoiceType() {
    return this.showFixedValues()
      && this.editedCheck.dataType !== apsEnums.CheckDataType.Text
      ? 'number'
      : 'text';
  }

  showFixedValues() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Text
      || this.editedCheck.dataType === apsEnums.CheckDataType.Decimal
      || this.editedCheck.dataType === apsEnums.CheckDataType.Integer;
  }

  showAggregates() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Decimal
      || this.editedCheck.dataType === apsEnums.CheckDataType.Integer
      || this.editedCheck.dataType === apsEnums.CheckDataType.Percent;
  }

  choicesInvalid() {
    return this.showFixedValues() && this.editedCheck.choices.length === 1;
  }

  showDefaultToToday() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.DateTime;
  }

  isInstructions() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Instructions;
  }

  isBoolean() {
    return this.editedCheck.dataType === apsEnums.CheckDataType.Boolean;
  }

  isSample() {
    return  this.editedCheck.valueType === apsEnums.CheckValueType.Sample;
  }

  isAggregate() {
    return  this.editedCheck.valueType === apsEnums.CheckValueType.Aggregate;
  }

  isMultiple() {
    return  !this.isInstructions() && this.editedCheck.valueType === apsEnums.CheckValueType.Multiple;
  }

  isMultipleSample() {
    return  this.editedCheck.valueType === apsEnums.CheckValueType.MultipleSample;
  }

  save() {
    if (this.form.$invalid || this.choicesInvalid()) {
      return;
    }

    var submit;
    if (this.isNew) {
      submit = this.checkListsService.addCheck;
    } else {
      submit = this.checkListsService.saveCheck;
    }

    var promise = submit(this.editedCheck).then(() => {
      this.$modalInst.close(this.editedCheck);
    }, this.notifyService.handleApiErrors);

    this.loaderService.loadingPromise(promise);
  }

  cancel() {
    this.$modalInst.close();
  }

  addChoice(): void {
    var choice = {
      id: undefined,
      value: ''
    };
    this.editedCheck.choices.push(choice);
    this.newGuidService.getNewGuid('checkChoice')
      .then((id: string) => {
        choice.id = id;
      });
  }

  removeChoice(choice: apsCommands.AddCheckChoice): void {
    _.remove(this.editedCheck.choices, choice);
  }

  addDimension(): void {
    this.editedCheck.dimensions.push({name: '', aggregateFunction: null});
  }

  removeDimension(index: number): void {
    this.editedCheck.dimensions.splice(index, 1);
  }

  dimsInvalid(): boolean {
    return this.editedCheck.valueType === apsEnums.CheckValueType.Multiple && this.editedCheck.dimensions.length < 2;
  }
}

export default angular.module('aps.controller.checkEditor', requires)
  .controller('CheckEditorCtrl', CheckEditorController)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/checkLists/checkEditor.controller.ts